import React, { useState, useEffect, useRef } from 'react';

const Projects = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const sectionRef = useRef(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const projects = [
    {
      id: 1,
      title: "eBallot Voting Platform",
      description: "Comprehensive election management system with real-time voting, candidate management, and role-based access control for Super Admin, Company Admin, and Sub Admin users.",
      technologies: ["React", "Node.js", "Express", "PostgreSQL", "Prisma"],
      features: ["Real-time Voting", "Excel Bulk Upload", "Multi-role Access", "Payment Integration"],
      category: "web",
      image: "/eballotbox.png",
      gradient: "from-blue-500 to-cyan-500",
      link: "https://eballotbox.org/",
      github: null
    },
    {
      id: 2,
      title: "Election Hub",
      description: "Advanced election management hub for organizing and monitoring multiple elections with real-time results, voter management, and comprehensive analytics dashboard.",
      technologies: ["React", "Node.js", "Express", "PostgreSQL", "Real-time APIs"],
      features: ["Multi-election Management", "Live Results", "Analytics Dashboard", "Voter Registration"],
      category: "web",
      image: "/electionhub.png",
      gradient: "from-purple-500 to-pink-500",
      link: "https://eballotbox.org/elections",
      github: null
    },
    {
      id: 3,
      title: "Core Banking System",
      description: "Enterprise-grade core banking solution with comprehensive account and customer management, transaction processing, and automated banking operations. Built with Oracle database for reliability and scalability.",
      technologies: ["Oracle Forms", "PL/SQL", "Oracle DB", "Java", "REST APIs"],
      features: ["Account Management", "Customer Management", "Transaction Processing", "Reporting & Analytics"],
      category: "enterprise",
      image: "/cbsystem.jpg",
      gradient: "from-indigo-500 to-purple-600",
      link: null,
      github: null
    },
    {
      id: 4,
      title: "Ghana Tourism Investment Platform",
      description: "Official tourism investment platform for Ghana featuring project profiles, investment opportunities, YouTube video integration, and comprehensive data management system approved by government authorities.",
      technologies: ["React", "Node.js", "YouTube API", "PostgreSQL", "REST APIs"],
      features: ["Investment Profiles", "Video Integration", "Data Management", "Export Reports"],
      category: "web",
      image: "/investment.png",
      gradient: "from-green-500 to-emerald-500",
      link: "https://investintourism.org/",
      github: null
    },
    {
      id: 5,
      title: "Mews AI - AI News Platform",
      description: "AI-driven news aggregation mobile app delivering personalized news feeds, intelligent content recommendations, and real-time updates across multiple categories. Available on iOS and Android.",
      technologies: ["Flutter", "Dart", "AI/ML APIs", "Firebase", "REST APIs"],
      features: ["AI Recommendations", "Personalized Feeds", "Real-time Updates", "Cross-platform"],
      category: "mobile",
      image: "/mewsai.jpg",
      gradient: "from-orange-500 to-red-500",
      links: [
        { label: "iOS App", url: "https://apps.apple.com/gh/app/mews-ai/id6476976884" },
        { label: "Android App", url: "https://play.google.com/store/apps/details?id=com.mews.mewsAI&pcampaignid=web_share" }
      ],
      github: null
    },
    {
      id: 6,
      title: "Biney's Cosmetics E-commerce Platform",
      description: "Full-featured e-commerce platform for Biney's Cosmetics with product management, shopping cart, secure checkout, and order tracking functionalities.",
      technologies: ["React", "Node.js", "Prisma", "MYSQL", "REST APIs"],
      features: ["Product Management", "Shopping Cart", "Secure Checkout", "Order Tracking"],
      category: "web",
      image: "/bineyscosmetics.png",
      gradient: "from-green-500 to-emerald-500",
      link: "https://bineyscosmetics.com/",
      github: null
    }
  ];

  const filters = [
    { label: 'All Projects', value: 'all' },
    { label: 'Web Apps', value: 'web' },
    { label: 'Enterprise', value: 'enterprise' },
    { label: 'Mobile Apps', value: 'mobile' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section ref={sectionRef} className="relative min-h-screen bg-white py-20 px-6 overflow-hidden">
      
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-purple-100/30 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-100/30 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 container mx-auto max-w-7xl mt-16">
        
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-purple-50 border border-purple-200 rounded-full text-purple-600 text-sm font-semibold mb-4 uppercase tracking-wide">
            Portfolio
          </span>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Featured{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
              Projects
            </span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-blue-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A selection of projects showcasing my expertise in full-stack development,
            payment integrations, and enterprise solutions.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className={`flex flex-wrap justify-center gap-3 mb-16 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {filters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setActiveFilter(filter.value)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                activeFilter === filter.value
                  ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {filteredProjects.map((project, index) => (
            <div 
              key={project.id}
              className={`group bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Project Image */}
              <div className="relative h-56 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    // Fallback to gradient background if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentElement.classList.add(`bg-gradient-to-br`, ...project.gradient.split(' '));
                  }}
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-white font-semibold text-lg">View Details</span>
                </div>
              </div>

              {/* Project Content */}
              <div className="p-8">
                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600 transition-colors">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {project.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Key Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="text-xs bg-gray-50 text-gray-700 px-3 py-2 rounded-lg border border-gray-200 font-medium"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="mb-8">
                  <h4 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Technologies</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => (
                      <span 
                        key={idx}
                        className={`text-xs bg-gradient-to-r ${project.gradient} text-white px-3 py-2 rounded-lg font-semibold shadow-sm`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  {/* Multiple links for mobile apps or single link */}
                  {project.links ? (
                    <>
                      {project.links.map((linkItem, idx) => (
                        <a 
                          key={idx}
                          href={linkItem.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 text-center px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                        >
                          <span>{linkItem.label}</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ))}
                    </>
                  ) : (
                    project.link && (
                      <a 
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <span>View Project</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )
                  )}
                  {project.github && (
                    <a 
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center px-6 py-3 bg-white border-2 border-gray-900 text-gray-900 rounded-xl font-semibold hover:bg-gray-900 hover:text-white transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      <span>GitHub</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-20 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {[
            { 
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ), 
              number: '50+', 
              label: 'Projects Completed' 
            },
            { 
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ), 
              number: '20+', 
              label: 'Happy Clients' 
            },
            { 
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              ), 
              number: '100%', 
              label: 'Success Rate' 
            },
            { 
              icon: (
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ), 
              number: '5+', 
              label: 'Years Experience' 
            }
          ].map((stat, index) => (
            <div 
              key={index}
              className="bg-white border-2 border-gray-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl hover:-translate-y-2 transition-all duration-300"
            >
              <div className="inline-flex p-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl mb-3">
                {stat.icon}
              </div>
              <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-2">
                {stat.number}
              </div>
              <div className="text-sm text-gray-600 font-semibold">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-3xl p-12 shadow-xl">
            <h3 className="text-4xl font-bold text-gray-900 mb-4">
              Have a Project in Mind?
            </h3>
            <p className="text-gray-700 text-lg mb-8 leading-relaxed">
              I'm always excited to take on new challenges and collaborate on innovative projects. 
              Let's discuss how I can help bring your ideas to life!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                Start a Project
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a 
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-white border-2 border-gray-900 text-gray-900 rounded-xl font-semibold text-lg hover:bg-gray-900 hover:text-white transition-all duration-300"
              >
                Get In Touch
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;